import { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { SpinningLoader } from 'common-components'
import { Tooltip } from 'components/Tooltip'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { COLORS } from 'consts/colors'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { useAvatarUpload } from '../hooks/useAvatarUpload'
import * as Styled from './styled'

const AvatarPreview: React.FC = () => {
  const avatar = useSelector(aiAvatarSelectors.avatar)
  const isAvatarLoading = useSelector(aiAvatarSelectors.isAvatarLoading)
  const { handleRemoveAvatar } = useAvatarUpload()

  // Track which url has finished loading. Deriving the loading state from this
  // (rather than a one-shot flag) re-arms the loader every time the selected
  // avatar changes, so we show the loader until the new image decodes instead of
  // briefly keeping the previous avatar on screen.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null)

  const url = avatar?.url
  const isImageLoading = !!url && loadedUrl !== url
  const showLoader = isAvatarLoading || isImageLoading

  return (
    <Styled.PreviewContainer data-testid="avatar-preview">
      {url && (
        <Styled.PreviewImage
          src={url}
          alt="Avatar"
          onLoad={() => setLoadedUrl(url ?? null)}
          onError={() => setLoadedUrl(url ?? null)}
        />
      )}

      {!showLoader && (
        <Tooltip tooltipText={translate(T.DELETE_LABEL)}>
          <Styled.DeleteButton
            type="button"
            onClick={handleRemoveAvatar}
            data-testid="avatar-preview-delete"
          >
            <Styled.DeleteIcon />
          </Styled.DeleteButton>
        </Tooltip>
      )}

      {showLoader && (
        <Styled.LoadingOverlay>
          <SpinningLoader
            size={27}
            wrapperSize={32}
            loaderColor={COLORS.primaryDark}
            strokeWidth={2}
          />
        </Styled.LoadingOverlay>
      )}
    </Styled.PreviewContainer>
  )
}

export default memo(AvatarPreview)
